import type { GridApi, MenuItemDef } from 'ag-grid-community';
import { defineMessages } from 'react-intl';
import { agGridMenuSeparator, type AgGridMenuItems } from '../components/BlotterTable/types';
import { IconName } from '../components/Icons/IconNames';
import type { IntlWithFormatter } from '../contexts/IntlContext';
import { MixpanelEvent } from '../tokens';

// Can't move to @talos/kyoko import due to circular dependency
// - Once Enum's are refactored we can consider fixing

export type ExpandCollapseGroupNode = {
  level: number;
  childrenAfterGroup?: readonly unknown[] | null;
};

export type ExpandCollapseGridApi = Pick<GridApi, 'collapseAll' | 'expandAll' | 'forEachNode' | 'setRowNodeExpanded'>;

export type ExpandCollapseMixpanelTracker = {
  track: (event: MixpanelEvent, properties?: Record<string, unknown>) => void;
};

export type GetExpandCollapseMenusParams = {
  node?: ExpandCollapseGroupNode | null;
  api: ExpandCollapseGridApi;
  mixpanel?: ExpandCollapseMixpanelTracker;
  intl?: IntlWithFormatter;
};

const messages = defineMessages({
  expandAll: {
    id: 'utils.expandAll',
    defaultMessage: 'Expand All',
  },
  collapseAll: {
    id: 'utils.collapseAll',
    defaultMessage: 'Collapse All',
  },
  expandCollapse: {
    id: 'utils.expandCollapse',
    defaultMessage: 'Expand / Collapse',
  },
  displayUpToThisLevel: {
    id: 'utils.displayUpToThisLevel',
    defaultMessage: 'Display up to this level',
  },
  displayUpToChildLevel: {
    id: 'utils.displayUpToChildLevel',
    defaultMessage: 'Display up to child level',
  },
});

const expandIcon = `<i class="ag-icon ${IconName.ListExpand}"/>`;
const collapseIcon = `<i class="ag-icon ${IconName.ListCollapse}"/>`;
export const EXPAND_COLLAPSE_LABEL = messages.expandCollapse.defaultMessage!;
export const EXPAND_ALL_LABEL = messages.expandAll.defaultMessage!;
export const COLLAPSE_ALL_LABEL = messages.collapseAll.defaultMessage!;
export const DISPLAY_UP_TO_THIS_LEVEL_LABEL = messages.displayUpToThisLevel.defaultMessage!;
export const DISPLAY_UP_TO_CHILD_LEVEL_LABEL = messages.displayUpToChildLevel.defaultMessage!;

export function expandAllUpToLevel(api: ExpandCollapseGridApi, upToLevel: number) {
  api.forEachNode(n => {
    if (n.level < upToLevel) {
      api.setRowNodeExpanded(n, true);
    } else if (n.level > upToLevel - 1) {
      api.setRowNodeExpanded(n, false);
    }
  });
}

/** Easy access to Expand/Collapse right click menu options */
export function getExpandCollapseMenus(params: GetExpandCollapseMenusParams): Array<MenuItemDef> {
  const node = params.node;
  if (node == null) {
    return [];
  }

  const targetLevel = node.level;
  const showThisLevel = targetLevel > 0;
  const showChildLevel = (node.childrenAfterGroup?.length ?? 0) > 0;
  const showScopedItems = showThisLevel || showChildLevel;

  const expandAllItem: MenuItemDef = {
    icon: expandIcon,
    name: params.intl?.formatMessage(messages.expandAll) ?? EXPAND_ALL_LABEL,
    action: () => {
      params.mixpanel?.track(MixpanelEvent.ExpandAllRows);
      params.api.expandAll();
    },
  };

  const collapseAllItem: MenuItemDef = {
    icon: collapseIcon,
    name: params.intl?.formatMessage(messages.collapseAll) ?? COLLAPSE_ALL_LABEL,
    action: () => {
      params.mixpanel?.track(MixpanelEvent.CollapseAllRows);
      params.api.collapseAll();
    },
  };

  const scopedExpandItems: AgGridMenuItems = [];
  if (showScopedItems) {
    scopedExpandItems.push(agGridMenuSeparator);
  }
  if (showThisLevel) {
    scopedExpandItems.push({
      icon: expandIcon,
      // TODO: In the future, we can make labels dynamic based on row group type (e.g. Market Account, Underlying)
      name: params.intl?.formatMessage(messages.displayUpToThisLevel) ?? DISPLAY_UP_TO_THIS_LEVEL_LABEL,
      action: () => {
        params.mixpanel?.track(MixpanelEvent.DisplayUpToThisLevel);
        expandAllUpToLevel(params.api, targetLevel);
      },
    });
  }
  if (showChildLevel) {
    scopedExpandItems.push({
      icon: expandIcon,
      name: params.intl?.formatMessage(messages.displayUpToChildLevel) ?? DISPLAY_UP_TO_CHILD_LEVEL_LABEL,
      action: () => {
        params.mixpanel?.track(MixpanelEvent.DisplayUpToChildLevel);
        expandAllUpToLevel(params.api, targetLevel + 1);
      },
    });
  }

  const expandCollapseMenuItem: MenuItemDef = {
    icon: expandIcon,
    name: params.intl?.formatMessage(messages.expandCollapse) ?? EXPAND_COLLAPSE_LABEL,
    subMenu: [expandAllItem, collapseAllItem, ...scopedExpandItems],
  };

  return [expandCollapseMenuItem];
}
